import { useState, useMemo, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import type { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { Chart, type ChartConfiguration } from 'chart.js';

import { GraphResponse } from '~/shared/types/api';
import { htmlLegendPlugin } from './htmlLegendPlugin';
import Legends, { ChartTypes } from './Legends';
import { ExternalTooltipHandler } from './ExternalTooltip';
import {
  calculateDateRange,
  checkIsSingleDayData,
  formatChartData,
  filterFutureDataPoints,
  CHART_COLORS,
  type VisibleDatasets,
  defaultVisibleDatasets,
  computeYScale,
  buildChartOptions,
  waitForChartExportFrames,
} from './helpers';

export interface LineChartHandle {
  getCanvas(): HTMLCanvasElement | null;
  exportWithData(data: GraphResponse['utilizationGraph']): Promise<Blob | null>;
}

type LegendItem = { label: string; color: string; dashed: boolean };

const EXPORT_IMAGE_WIDTH = 800;
const EXPORT_CHART_HEIGHT = 350;
const EXPORT_PIXEL_RATIO = 2;

const LineChartImpl = forwardRef<
  LineChartHandle,
  {
    data: GraphResponse['utilizationGraph'];
    activeFilter: string;
    chartType?: ChartTypes;
  }
>(function LineChartImpl({ data, chartType = 'utilization' }, ref) {
  const filteredData = useMemo(() => filterFutureDataPoints(data), [data]);

  const hasDatafyDiskUsed = useMemo(
    () => !!filteredData?.some((d) => typeof d.datafyDiskUsed === 'number'),
    [filteredData]
  );

  const [visibleDatasets, setVisibleDatasets] = useState<VisibleDatasets>(() =>
    defaultVisibleDatasets(chartType, hasDatafyDiskUsed)
  );

  useEffect(() => {
    setVisibleDatasets((prev) => ({
      ...prev,
      utilized: chartType === 'utilization' || chartType === 'usage',
      datafyDiskUsed: hasDatafyDiskUsed || prev.datafyDiskUsed,
      sourceSize: chartType === 'usage',
      size: chartType === 'usage',
    }));
  }, [chartType, hasDatafyDiskUsed]);

  const toggleDatasetVisibility = useCallback((datasetKey: keyof VisibleDatasets) => {
    setVisibleDatasets((prev) => ({ ...prev, [datasetKey]: !prev[datasetKey] }));
  }, []);

  const [minDate, maxDate] = useMemo(() => calculateDateRange(filteredData), [filteredData]);
  const isSingleDayData = useMemo(() => checkIsSingleDayData(minDate, maxDate), [minDate, maxDate]);

  const { actualMaxVal, yMaxForScale } = useMemo(
    () => computeYScale(filteredData, visibleDatasets, chartType),
    [filteredData, visibleDatasets, chartType]
  );

  const chartData = useMemo(
    () => formatChartData(filteredData, visibleDatasets, chartType),
    [filteredData, visibleDatasets, chartType]
  );

  const options = useMemo(
    () => buildChartOptions(minDate, maxDate, isSingleDayData, yMaxForScale, actualMaxVal, chartType, ExternalTooltipHandler),
    [minDate, maxDate, isSingleDayData, yMaxForScale, actualMaxVal, chartType]
  );

  const chartRef = useRef<ChartJSOrUndefined<'line'>>(null);

  useImperativeHandle(ref, () => ({
    getCanvas: () => chartRef.current?.canvas ?? null,
    exportWithData: async (rawData: GraphResponse['utilizationGraph']): Promise<Blob | null> => {
      const filtered = filterFutureDataPoints(rawData);
      const hasDatafy = !!filtered?.some((d) => typeof d.datafyDiskUsed === 'number');
      const exportVisible = defaultVisibleDatasets(chartType, hasDatafy);
      const exportChartData = formatChartData(filtered, exportVisible, chartType);

      const [minD, maxD] = calculateDateRange(filtered);
      const isSingleDay = checkIsSingleDayData(minD, maxD);
      const { actualMaxVal: expMax, yMaxForScale: expYMax } = computeYScale(filtered, exportVisible, chartType);
      const exportOptions = {
        ...buildChartOptions(minD, maxD, isSingleDay, expYMax, expMax, chartType, undefined, true),
        devicePixelRatio: EXPORT_PIXEL_RATIO,
      };

      const offscreen = document.createElement('canvas');
      offscreen.width = EXPORT_IMAGE_WIDTH;
      offscreen.height = EXPORT_CHART_HEIGHT;
      offscreen.style.width = `${EXPORT_IMAGE_WIDTH}px`;
      offscreen.style.height = `${EXPORT_CHART_HEIGHT}px`;

      const config = {
        type: 'line',
        data: exportChartData,
        options: exportOptions,
      } as ChartConfiguration<'line', unknown, unknown>;
      const tempChart = new Chart(offscreen, config);
      try {
        await waitForChartExportFrames();

        const TITLE_HEIGHT = 40;
        const LEGEND_HEIGHT = 36;
        const HEADER_HEIGHT = TITLE_HEIGHT + LEGEND_HEIGHT;
        const EXPORT_IMAGE_HEIGHT = EXPORT_CHART_HEIGHT + HEADER_HEIGHT;
        const composite = document.createElement('canvas');
        composite.width = EXPORT_IMAGE_WIDTH * EXPORT_PIXEL_RATIO;
        composite.height = EXPORT_IMAGE_HEIGHT * EXPORT_PIXEL_RATIO;
        composite.style.width = `${EXPORT_IMAGE_WIDTH}px`;
        composite.style.height = `${EXPORT_IMAGE_HEIGHT}px`;
        const ctx = composite.getContext('2d');
        if (!ctx) {
          throw new Error('Could not acquire 2D canvas context for export composite');
        }

        ctx.scale(EXPORT_PIXEL_RATIO, EXPORT_PIXEL_RATIO);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, EXPORT_IMAGE_WIDTH, EXPORT_IMAGE_HEIGHT);

        const title = chartType === 'utilization' ? 'Utilization Chart' : 'Usage Chart';
        ctx.font = 'bold 16px Inter, sans-serif';
        ctx.fillStyle = '#0E3C48';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillText(title, EXPORT_IMAGE_WIDTH / 2, TITLE_HEIGHT / 2);

        const legendItems: LegendItem[] = [];
        if (chartType === 'utilization') {
          legendItems.push({ label: 'Utilization %', color: CHART_COLORS.UTILIZED, dashed: false });
        } else {
          if (exportVisible.datafyDiskUsed) {
            legendItems.push({ label: 'Datafy Used', color: CHART_COLORS.DATAFY_DISK_USED, dashed: false });
          }
          legendItems.push({ label: 'Used', color: CHART_COLORS.UTILIZED, dashed: false });
          legendItems.push({ label: 'Size', color: CHART_COLORS.SIZE, dashed: false });
          legendItems.push({ label: 'Original Size', color: CHART_COLORS.SOURCE_SIZE, dashed: true });
        }

        ctx.font = 'bold 12px Inter, sans-serif';
        ctx.textAlign = 'left';
        const ICON_WIDTH = 12;
        const ICON_HEIGHT = 4;
        const GAP = 6;
        const ITEM_SPACING = 24;
        const itemWidths = legendItems.map((item) => ICON_WIDTH + GAP + ctx.measureText(item.label).width);
        const totalWidth =
          itemWidths.reduce((sum, w) => sum + w, 0) + ITEM_SPACING * (legendItems.length - 1);
        let x = (EXPORT_IMAGE_WIDTH - totalWidth) / 2;
        const y = TITLE_HEIGHT + LEGEND_HEIGHT / 2;

        for (let i = 0; i < legendItems.length; i++) {
          const item = legendItems[i];
          ctx.strokeStyle = item.color;
          ctx.lineWidth = 4;
          if (item.dashed) {
            ctx.setLineDash([4, 2]);
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + ICON_WIDTH, y);
            ctx.stroke();
            ctx.setLineDash([]);
          } else {
            ctx.fillStyle = item.color;
            ctx.fillRect(x, y - ICON_HEIGHT / 2, ICON_WIDTH, ICON_HEIGHT);
          }
          ctx.fillStyle = '#949EAC';
          ctx.fillText(item.label, x + ICON_WIDTH + GAP, y);
          x += itemWidths[i] + ITEM_SPACING;
        }

        ctx.drawImage(offscreen, 0, HEADER_HEIGHT, EXPORT_IMAGE_WIDTH, EXPORT_CHART_HEIGHT);

        return await new Promise<Blob | null>((resolve) => composite.toBlob(resolve, 'image/png'));
      } finally {
        tempChart.destroy();
      }
    },
  }), [chartType]);

  return (
    <div className="h-full flex flex-col">
      <Legends
        onLabelToggle={toggleDatasetVisibility}
        visibilityStatus={visibleDatasets}
        chartType={chartType}
      />
      <Line
        ref={chartRef}
        className="h-[276px]! max-w-full"
        options={options}
        data={chartData}
        plugins={[htmlLegendPlugin]}
      />
    </div>
  );
});

export default LineChartImpl;
